import { Icon } from "minimum-ui/core";
import { color } from "minimum-ui/tokens";
import styled from "styled-components";
import { Colors } from "theme/colors";

interface Props {
  tabIndex?: number;
  isSelected?: boolean;
  title?: string;
  dataTestId?: string;
  truncateLineCount?: number;
  padding?: string;
  hasError?: boolean;
  onClick: () => void;
}

interface PropsWithChildren extends Props {
  children: React.ReactNode;
}

interface PropsWithText extends Props {
  text: string;
}

/**
 * A generic menu item component.
 *
 * It can be used with either a `children` property to pass in a React node,
 * or a `text` property to pass in a string of text.
 *
 * @prop {number} [tabIndex=0] - The tab index of the element.
 * @prop {boolean} [isSelected=false] - Whether the item is selected.
 * @prop {string} [title] - The title of the element.
 * @prop {string} [dataTestId] - The data test ID of the element.
 * @prop {number} [truncateLineCount=1] - The number of lines to truncate the text to.
 * @prop {string} [padding="0.25rem 0.5rem"] - The padding of the element.
 * @prop {React.ReactNode} children - The children of the element.
 * @prop {string} text - The text of the element.
 * @prop {boolean} [hasError=false] - Whether the item has an error state.
 * @prop {() => void} onClick - The click handler of the element.
 */
export const MenuItem: React.FC<PropsWithChildren | PropsWithText> = (
  props
) => {
  const {
    tabIndex = 0,
    isSelected,
    title,
    dataTestId,
    truncateLineCount = 1,
    padding = "0.25rem 0.5rem",
    hasError,
    onClick,
  } = props;
  const children = (props as PropsWithChildren).children;
  const text = (props as PropsWithText).text;

  return (
    <StyledButton
      tabIndex={tabIndex}
      data-testid={dataTestId}
      isSelected={isSelected}
      title={title}
      truncateLineCount={truncateLineCount}
      padding={padding}
      onClick={onClick}
    >
      <ContentLayout>{text ?? children}</ContentLayout>

      {hasError && (
        <span>
          <Icon icon="circle" size="2xs" color={color.red[400]} />
        </span>
      )}
    </StyledButton>
  );
};

const StyledButton = styled.button<{
  isSelected?: boolean;
  truncateLineCount: number;
  padding?: string;
}>`
  all: unset;
  cursor: pointer;
  color: inherit;
  font: inherit;

  flex: 1;
  font-size: 0.875rem;
  cursor: pointer;
  border-radius: 0.25rem;
  padding: ${(props) => props.padding};
  background: ${(props) =>
    props.isSelected ? Colors.border.default : "transparent"};
  line-height: 1.25rem;
  transition: background 0.3s ease, color 0.2s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: ${(props) => props.truncateLineCount};
  -webkit-box-orient: vertical;
  white-space: normal;
  display: flex;

  &:hover {
    background: ${Colors.border.default};
  }

  &:active {
    background: ${color.neutral[400]};
  }

  &:focus-visible {
    outline: 1px dashed ${Colors.border.default};
    outline-offset: -1px;
  }
`;

const ContentLayout = styled.div`
  flex: 1;
`;
